import {useEffect, useRef, useState} from 'react';
import {getAnnotations} from '../../api/dataset/GetAnnotations';
import {deploymentExecute, testExecute} from '../../api/gsp/ExecuteUI';
import {CategoryType} from '../../types/dataset/DatasetGetAnnotationsResponse';
import {RawInferenceResponse} from '../../types/gsp/RawInferenceResponse';
import {Application} from '../../types/project/Application';
import {SinglePageApplication} from '../../types/project/Application';
import {usePostProcessingScript} from '../PrePostProcessing/PostProcessing/usePostProcessingScript';
import {isPathScript} from '../ONNXRuntimeContainer/ONNXRuntimeContainer';

type OpenVXRuntimeContainerProps = {
  projectId: string;
  application: Application;
  spa: SinglePageApplication;
  images?: File[];
  inProgress: boolean;
  onFinish?: () => void;
  onRunning?: () => void;
  onError?: (message?: string) => void;
};

export const OpenVXRuntimeContainer = ({
  projectId,
  application,
  spa,
  images,
  inProgress,
  onError,
  onFinish,
  onRunning,
}: OpenVXRuntimeContainerProps) => {
  const onErrorRef = useRef(onError);
  const onFinishRef = useRef(onFinish);
  const onRunningRef = useRef(onRunning);
  const [labels, setLabels] = useState<CategoryType[]>();
  const [inferenceOutput, setInferenceOutput] = useState<RawInferenceResponse>();
  const appPostProcessor = application.postProcessors?.find(
    pp => pp.id === spa.postProcessorId
  );

  const fileName = appPostProcessor?.filePaths?.find(isPathScript);

  const module = usePostProcessingScript({
    projectId,
    applicationId: spa.id,
    id: spa.postProcessorId,
    hasPostProcessors: application?.postProcessors !== undefined,
    fileName,
    postProcessorType: 'APPLICATION',
    executionContainer: 'BROWSER',
    viewOnly: true,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchAnnotations = async () => {
      try {
        if (!application.dataSet) {
          return;
        }
        const {data} = await getAnnotations({
          applicationId: application.id,
        });
        !cancelled && data.categories && setLabels(data.categories);
      } catch (e) {
        onErrorRef.current?.();
        console.error(e);
      }
    };

    fetchAnnotations();

    return () => {
      cancelled = true;
    };
  }, [application]);

  useEffect(() => {
    let cancelled = false;
    async function runInference() {
      let response;

      onRunningRef.current?.();

      if (spa.target.type === 'TEST' && images) {
        response = await testExecute({
          spaId: spa.id,
          files: images,
          targetDeviceType: 'GSP',
        });
      } else if (spa.target.type === 'PRODUCTION' && images) {
        response = await deploymentExecute({
          spaId: spa.id,
          files: images,
        });
      }

      if (!cancelled) {
        if (response?.body && !response?.errors?.length) {
          setInferenceOutput(response.body);
          onFinishRef.current?.();
        } else {
          onErrorRef.current?.();
        }
      }
    }

    if (inProgress) {
      runInference();
    }

    return () => {
      cancelled = true;
    };
  }, [images, spa.id, spa.target.type, inProgress]);

  useEffect(() => {
    if (module) {
      try {
        if ('postProcess' in module) {
          if (inferenceOutput && images) {
            labels && module.readAnnotations && module.readAnnotations(labels);
            module.postProcess && module.postProcess(inferenceOutput, labels, images);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [module, inferenceOutput, images, labels]);

  return null;
};
